import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "seven7h_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (_) {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return; // avoid wiping storage on first render
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_) {}
  }, [items, hydrated]);

  const add = (product, qty = 1) => {
    setItems((cur) => {
      const found = cur.find((c) => c.slug === product.slug);
      if (found) {
        return cur.map((c) =>
          c.slug === product.slug ? { ...c, quantity: Math.min(99, c.quantity + qty) } : c
        );
      }
      return [
        ...cur,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          price: product.sale_price ?? product.price,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const setQty = (slug, qty) =>
    setItems((cur) =>
      qty <= 0
        ? cur.filter((c) => c.slug !== slug)
        : cur.map((c) => (c.slug === slug ? { ...c, quantity: Math.min(99, qty) } : c))
    );

  const remove = (slug) => setItems((cur) => cur.filter((c) => c.slug !== slug));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = subtotal >= 200 || subtotal === 0 ? 0 : 15;
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, shipping, total: subtotal + shipping, count };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, add, setQty, remove, clear, open, setOpen, ...totals }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
